/**
 * People named in missions and items as a reader expects: "you" for the person signed in, a
 * name instead of an account id, Orgus for changes it made itself.
 */
import { useCallback } from 'react';
import { useMe } from '../auth/useMe.ts';

export interface Who {
  /** The signed-in actor id and login, when known. */
  me?: { id: string; login?: string | null; name?: string | null } | null;
}

/** "you", "Orgus", or the id without its "agent:" prefix. Pure, so it is tested. */
export function personName(id: string | null | undefined, who: Who = {}): string {
  if (!id) return 'someone';
  const me = who.me;
  if (me && (id === me.id || (me.login && id.toLowerCase() === me.login.toLowerCase()))) return 'you';
  if (id === 'orgus' || id === 'system') return 'Orgus';
  if (id === 'owner') return 'the owner';
  if (id.startsWith('mission:')) return 'a mission';
  return id.startsWith('agent:') ? id.slice(6) : id;
}

/** personName bound to the signed-in person. */
export function usePersonName(): (id: string | null | undefined) => string {
  const me = useMe((s) => s.me);
  const id = me?.actor.id;
  const login = me?.member?.login ?? null;
  const name = me?.member?.name ?? null;
  return useCallback((x: string | null | undefined) => personName(x, { me: id ? { id, login, name } : null }), [id, login, name]);
}
